import React from 'react';

import { Botao, Chip, Modal, Nota } from '../../../ui';
import { VistoriaEnvioSga } from '../../../api/sga-vistoria/sga-vistoriaApi';
import { formatarDataCurta } from '../../../utils/data';
import { rotuloEnvioSga } from '../../../utils/vistoriaFluxo';
import { NOTA_ENVIO_AUTOMATICO } from '../../../utils/envioSgaRegras';

export interface ModalDetalhesEnvioVistoriaProps {
  /** Vistoria em exibição — null mantém o modal fechado. */
  vistoria: VistoriaEnvioSga | null;
  ocupado: boolean;
  /** Chave da ação em curso ("vistoria:<id>" / "fotos:<id>"). */
  enviando: string | null;
  onFechar: () => void;
  onEnviarVistoria: (vistoria: VistoriaEnvioSga) => void;
  onEnviarFotos: (vistoria: VistoriaEnvioSga) => void;
  onVerEnvio: (vistoria: VistoriaEnvioSga) => void;
}

/**
 * Detalhes de uma vistoria do acompanhamento de envios (at-32; reescrito na at-70).
 *
 * ⚠️ **Os chips de laudo e fotos vêm de `rotuloEnvioSga`** (`utils/vistoriaFluxo.ts`),
 * a MESMA fonte do painel `/vistoria`, do modal de detalhes de lá e da carteira. É
 * regra da at-63: as telas **nunca podem divergir sobre a mesma vistoria**. Aqui
 * elas divergiam — este modal dizia "Laudo enviado" em verde e "Laudo pendente" no
 * laranja da marca, por um `ChipEnvio` próprio.
 *
 * 📌 A **placa** de motocicleta elétrica ainda sai como placa nesta tela: a resposta
 * de `GET /sga-vistoria/acompanhamento` não traz o campo `bikeEletrica` (a de
 * associados traz). Inferir pelo prefixo `BIK` seria adivinhação (at-54) — é uma
 * linha de backend, registrada como pendência junto ao **B16** em `12-bugs.md`.
 */
const ModalDetalhesEnvioVistoria: React.FC<ModalDetalhesEnvioVistoriaProps> = ({
  vistoria,
  ocupado,
  enviando,
  onFechar,
  onEnviarVistoria,
  onEnviarFotos,
  onVerEnvio,
}) => {
  if (!vistoria) return null;

  const laudoPendente = !vistoria.addedToSga;
  const fotosPendentes = !vistoria.imagesAddedToSga;
  // As fotos exigem `codigo_veiculo` no SGA — sem ele o envio nem é tentado.
  const semCodigoVeiculo = !vistoria.codigoVeiculo;

  const laudo = rotuloEnvioSga(vistoria.addedToSga, 'Laudo');
  const fotos = rotuloEnvioSga(vistoria.imagesAddedToSga, 'Fotos');

  return (
    <Modal
      aberto
      titulo={vistoria.associado || 'Vistoria'}
      descricao={[vistoria.placa, vistoria.modelo].filter(Boolean).join(' · ')}
      tamanho="lg"
      ocupado={enviando !== null}
      aoFechar={onFechar}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Envio ao SGA
          </h3>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <Chip variante={laudo.variante}>{laudo.texto}</Chip>
            <Chip variante={fotos.variante}>{fotos.texto}</Chip>
          </div>

          {(laudoPendente || fotosPendentes) && (
            <Nota className="tw-mt-3">{NOTA_ENVIO_AUTOMATICO}</Nota>
          )}

          <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-flex-wrap">
            {laudoPendente && (
              <Botao
                variante="primario"
                tamanho="sm"
                disabled={ocupado}
                carregando={enviando === `vistoria:${vistoria.id}`}
                onClick={() => onEnviarVistoria(vistoria)}
              >
                Enviar laudo
              </Botao>
            )}
            {fotosPendentes && (
              <Botao
                variante="primario"
                tamanho="sm"
                disabled={ocupado || semCodigoVeiculo}
                carregando={enviando === `fotos:${vistoria.id}`}
                onClick={() => onEnviarFotos(vistoria)}
              >
                Enviar fotos
              </Botao>
            )}
            <Botao
              variante="secundario"
              tamanho="sm"
              disabled={ocupado}
              onClick={() => onVerEnvio(vistoria)}
            >
              Ver envio
            </Botao>
          </div>

          {/* 🔴 O motivo da trava em TEXTO — era `title` no botão. Não é falha: o
              cron manda as fotos assim que o veículo subir. */}
          {fotosPendentes && semCodigoVeiculo && (
            <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
              As fotos só sobem depois que o veículo estiver no SGA — o cron as envia assim que ele
              subir.
            </p>
          )}
        </section>

        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Registro
          </h3>

          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-texto-suave">
            <span>Consultor: {vistoria.consultor || '—'}</span>
            <span>Cadastrada em: {formatarDataCurta(vistoria.createdat)}</span>
            <span>Código do associado no SGA: {vistoria.codigoAssociado || '—'}</span>
            <span>Código do veículo no SGA: {vistoria.codigoVeiculo || '—'}</span>
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ModalDetalhesEnvioVistoria;
